// Whitepaper: Pragmatische Digitalisierung — Kallideus 2026
// Modal content indexed to match the 3 KPI cards in benefits.jsx

export const BENEFIT_MODAL_DATA = [
  {
    // Card 1 — build / -30% / Instandhaltungskosten → Chapter 6
    icon: "build",
    value: "-30%",
    label: { de: "Instandhaltungskosten", en: "Maintenance Costs", pl: "Koszty utrzymania" },
    chapter: {
      de: "Effektives Instandhaltungsmanagement",
      en: "Effective Maintenance Management",
      pl: "Efektywne zarządzanie utrzymaniem ruchu",
    },
    subtitle: {
      de: "Einsparungen durch Prognostizierbarkeit",
      en: "Savings through Predictability",
      pl: "Oszczędności dzięki predyktywności",
    },
    highlights: [
      {
        value: "−50%",
        label: { de: "Maschinenstillstände", en: "Machine Downtime", pl: "Przestoje maszyn" },
        source: { de: "durch Predictive Maintenance", en: "via Predictive Maintenance", pl: "dzięki Predictive Maintenance" },
      },
      {
        value: "−40%",
        label: { de: "Wartungskosten", en: "Maintenance Costs", pl: "Koszty serwisu" },
        source: { de: "bedarfsgerechte statt reaktive Wartung", en: "demand-driven vs. reactive", pl: "serwis na żądanie vs reaktywny" },
      },
      {
        value: "+40%",
        label: { de: "Asset-Lebensdauer", en: "Asset Lifetime", pl: "Żywotność aktywów" },
        source: { de: "durch Früherkennung von Verschleiß", en: "via early wear detection", pl: "przez wczesne wykrywanie zużycia" },
      },
    ],
    bullets: {
      de: [
        "Predictive Maintenance: Sensoren und Algorithmen erkennen subtile Vorboten eines Ausfalls, bevor er eintritt. Maschinen fallen selten plötzlich aus – sie verschleißen messbar.",
        "Paradigmenwechsel: Der Wandel von reaktiver zu prädiktiver Instandhaltung reduziert das Ausfallrisiko um 50 % und verlängert die Asset-Lebensdauer um bis zu 40 %.",
        "Lageroptimierung: Mit prognostizierter Instandhaltung können Ersatzteile Just-in-Time bestellt werden – das reduziert gebundenes Betriebskapital erheblich.",
        "Amortisation: Programme zur vorausschauenden Wartung amortisieren sich innerhalb von 6 bis 18 Monaten allein durch Reduzierung von Ausfallzeiten und Notfallreparaturen.",
      ],
      en: [
        "Predictive Maintenance: Sensors and algorithms detect subtle precursors of failure before it occurs. Machines rarely fail suddenly – they wear measurably.",
        "Paradigm Shift: The shift from reactive to predictive maintenance reduces failure risk by 50% and extends asset lifetime by up to 40%.",
        "Inventory Optimisation: With predictive maintenance, spare parts can be ordered just-in-time – significantly reducing tied-up working capital.",
        "ROI: Predictive maintenance programmes pay for themselves within 6–18 months through reduced downtime and emergency repairs alone.",
      ],
      pl: [
        "Predictive Maintenance: Czujniki i algorytmy wykrywają subtelne oznaki awarii zanim do niej dojdzie. Maszyny rzadko psują się nagle – zużywają się mierzalnie.",
        "Zmiana paradygmatu: Przejście od reaktywnego do predyktywnego utrzymania ruchu zmniejsza ryzyko awarii o 50% i wydłuża żywotność aktywów o 40%.",
        "Optymalizacja magazynu: Dzięki prognozowaniu utrzymania ruchu, części zamienne mogą być zamawiane just-in-time – znacznie redukując zamrożony kapitał obrotowy.",
        "Amortyzacja: Programy predyktywnego utrzymania ruchu zwracają się w ciągu 6–18 miesięcy poprzez redukcję przestojów i napraw awaryjnych.",
      ],
    },
    table: {
      caption: {
        de: "Finanzielle Gegenüberstellung der Instandhaltungsstrategien",
        en: "Financial Comparison of Maintenance Strategies",
        pl: "Finansowe porównanie strategii utrzymania ruchu",
      },
      headers: {
        de: ["Strategie", "Kostenprofil", "Ausfallrisiko", "Lebensdauer"],
        en: ["Strategy", "Cost Profile", "Failure Risk", "Asset Life"],
        pl: ["Strategia", "Profil kosztów", "Ryzyko awarii", "Żywotność"],
      },
      rows: {
        de: [
          ["Reaktiv", "Am höchsten", "Hoch / unvorhersehbar", "Negativ"],
          ["Präventiv", "Moderat", "Moderat", "Neutral"],
          ["Prädiktiv ✓", "Am niedrigsten", "Minimiert (−50 %)", "Verlängert (+40 %)"],
        ],
        en: [
          ["Reactive", "Highest", "High / Unpredictable", "Negative"],
          ["Preventive", "Moderate", "Moderate", "Neutral"],
          ["Predictive ✓", "Lowest", "Minimised (−50 %)", "Extended (+40 %)"],
        ],
        pl: [
          ["Reaktywne", "Najwyższy", "Wysokie / Nieprzewidywalne", "Negatywna"],
          ["Prewencyjne", "Umiarkowany", "Umiarkowane", "Neutralna"],
          ["Predyktywne ✓", "Najniższy", "Zminimalizowane (−50 %)", "Wydłużona (+40 %)"],
        ],
      },
    },
  },

  {
    // Card 2 — factory / +15% / OEE → Chapter 3
    icon: "factory",
    value: "+15%",
    label: { de: "Gesamtanlageneffektivität (OEE)", en: "Overall Equipment Effectiveness", pl: "Ogólna efektywność urządzeń (OEE)" },
    chapter: {
      de: "Maximierung der Fertigungseffizienz",
      en: "Maximising Manufacturing Efficiency",
      pl: "Maksymalizacja efektywności produkcji",
    },
    subtitle: {
      de: "Der Motor der Wertschöpfung",
      en: "The Engine of Value Creation",
      pl: "Silnik tworzenia wartości",
    },
    highlights: [
      {
        value: "+15%",
        label: { de: "OEE-Verbesserung", en: "OEE Improvement", pl: "Poprawa OEE" },
        source: { de: "mit MES", en: "with MES", pl: "z MES" },
      },
      {
        value: "−42%",
        label: { de: "Chargenvariabilität", en: "Batch Variability", pl: "Zmienność partii" },
        source: { de: "durch Standardisierung via MES", en: "via MES standardisation", pl: "przez standaryzację w MES" },
      },
      {
        value: "−20%",
        label: { de: "Ausschussrate", en: "Scrap Rate", pl: "Wskaźnik odpadów" },
        source: { de: "durch digitale Qualitätskontrolle", en: "via digital quality control", pl: "przez cyfrową kontrolę jakości" },
      },
    ],
    bullets: {
      de: [
        "Automatisierte Datenerfassung: Manuelle Logbücher entfallen. Echtzeit-Erfassung eliminiert den menschlichen Filter – die aufgezeichnete Realität entspricht der physischen.",
        "Versteckte Fabrik sichtbar machen: Digitalisierung deckt nicht erfassten Ausschuss und Mikrostillstände auf, die 20–40 % der Produktionskapazität beanspruchen können.",
        "Standardisierte Arbeitsabläufe: MES setzt Standardarbeitsanweisungen durch und weist Produktionsaufgaben effizient zu – Studien zeigen eine Reduzierung der Chargenvariabilität um 42,7 %.",
        "Digitale Zwillinge: Virtuelle Abbilder der Fertigung ermöglichen Simulation und Optimierung im digitalen Raum – Siemens EWA reduzierte so den Materialumschlag um 40 % und Energieverbrauch um 70 %.",
      ],
      en: [
        "Automated Data Capture: Manual logbooks are eliminated. Real-time capture removes the human filter – recorded reality matches physical reality.",
        "Uncovering the Hidden Factory: Digitalisation reveals unrecorded scrap and micro-stoppages that can consume 20–40% of production capacity.",
        "Standardised Workflows: MES enforces standard work instructions and assigns tasks efficiently – studies show a 42.7% reduction in batch variability.",
        "Digital Twins: Virtual replicas of manufacturing enable simulation and optimisation in the digital space – Siemens EWA reduced material throughput by 40% and energy by 70%.",
      ],
      pl: [
        "Automatyczne zbieranie danych: Papierowe dzienniki znikają. Rejestracja w czasie rzeczywistym eliminuje ludzki filtr – zapisana rzeczywistość odpowiada fizycznej.",
        "Ujawnianie ukrytej fabryki: Digitalizacja odkrywa nieodnotowane odpady i mikroprzestoje, które mogą pochłaniać 20–40% zdolności produkcyjnych.",
        "Standaryzowane przepływy pracy: MES egzekwuje standardowe instrukcje pracy – badania pokazują redukcję zmienności partii o 42,7%.",
        "Cyfrowe bliźniaki: Wirtualne odwzorowania produkcji umożliwiają symulację i optymalizację w przestrzeni cyfrowej.",
      ],
    },
    table: {
      caption: {
        de: "Effizienzsteigerungen in der Produktion durch Digitalisierung",
        en: "Production Efficiency Gains through Digitalisation",
        pl: "Wzrost efektywności produkcji dzięki cyfryzacji",
      },
      headers: {
        de: ["Kennzahl", "Traditionell", "Digitalisiert", "Verbesserung"],
        en: ["Metric", "Traditional", "Digitalised", "Improvement"],
        pl: ["Wskaźnik", "Tradycyjnie", "Cyfrowo", "Poprawa"],
      },
      rows: {
        de: [
          ["OEE", "60 %–75 %", "80 %–85 %+", "10–25 %"],
          ["Ausschussrate", "Variabel / Hoch", "Minimiert", "~20 % Reduktion"],
          ["Chargenvariabilität", "Hoch", "Standardisiert", "42,7 % Reduktion"],
          ["Energieeffizienz", "Basiswert", "Optimiert (Digital Twin)", "25 %–70 %"],
          ["Prozessfähigkeit (Cpk)", "Basiswert", "Verbesserte Steuerung", "31,2 % Steigerung"],
        ],
        en: [
          ["OEE", "60%–75%", "80%–85%+", "10–25%"],
          ["Scrap Rate", "Variable / High", "Minimised", "~20% reduction"],
          ["Batch Variability", "High", "Standardised", "42.7% reduction"],
          ["Energy Efficiency", "Baseline", "Optimised (Digital Twin)", "25%–70%"],
          ["Process Capability (Cpk)", "Baseline", "Improved Control", "31.2% increase"],
        ],
        pl: [
          ["OEE", "60%–75%", "80%–85%+", "10–25%"],
          ["Wskaźnik odpadów", "Zmienny / Wysoki", "Zminimalizowany", "~20% redukcja"],
          ["Zmienność partii", "Wysoka", "Standaryzowana", "42,7% redukcja"],
          ["Efektywność energetyczna", "Wartość bazowa", "Zoptymalizowana", "25%–70%"],
          ["Zdolność procesu (Cpk)", "Wartość bazowa", "Lepsza kontrola", "31,2% wzrost"],
        ],
      },
    },
  },

  {
    // Card 3 — manage_history / 50% / Schnellere Prozesse → Chapters 4 + 5
    icon: "manage_history",
    value: "50%",
    label: { de: "Schnellere Prozesse", en: "Faster Processes", pl: "Szybsze procesy" },
    chapter: {
      de: "Personaloptimierung & Rückverfolgbarkeit",
      en: "Workforce Optimisation & Traceability",
      pl: "Optymalizacja personelu i identyfikowalność",
    },
    subtitle: {
      de: "Digitaler Schutzschild für Audits und Regelkonformität",
      en: "Digital Shield for Audits and Compliance",
      pl: "Cyfrowa tarcza dla audytów i zgodności",
    },
    highlights: [
      {
        value: "−40%",
        label: { de: "Einarbeitungszeit", en: "Onboarding Time", pl: "Czas wdrożenia" },
        source: { de: "durch digitale Arbeitsanweisungen", en: "via digital work instructions", pl: "dzięki cyfrowym instrukcjom pracy" },
      },
      {
        value: "−50%",
        label: { de: "Audit-Vorbereitungszeit", en: "Audit Prep Time", pl: "Czas przygotowania audytu" },
        source: { de: "durch MES-Dokumentation", en: "via MES documentation", pl: "dzięki dokumentacji MES" },
      },
      {
        value: "+30%",
        label: { de: "Arbeitsproduktivität", en: "Labour Productivity", pl: "Wydajność pracy" },
        source: { de: "durch vernetzte Mitarbeiter", en: "via connected workers", pl: "przez połączonych pracowników" },
      },
    ],
    bullets: {
      de: [
        "Qualifikationslücke schließen: Digitale Arbeitsanweisungen reduzieren die Einarbeitungszeit um 40 % und demokratisieren Fachwissen – ein Anfänger agiert mit der Präzision eines Experten.",
        "Vernetzter Mitarbeiter: Die Plattform leitet Techniker anhand von Echtzeit-Sensordaten direkt dorthin, wo Handlungsbedarf besteht – Produktivitätssteigerung von 15–30 %.",
        "Permanente Auditbereitschaft: Alle Datensätze sind mit Zeitstempel versehen, unveränderlich (gemäß FDA 21 CFR Part 11) und sofort abrufbar. Audit-Vorbereitung sinkt um 50 %.",
        "Präzise Rückrufe: Digitale Rückverfolgbarkeit begrenzt den Rückruf auf exakt betroffene Chargen – Identifizierung in Sekunden statt Tagen, massive Kosteneinsparung.",
      ],
      en: [
        "Closing the Skills Gap: Digital work instructions reduce onboarding time by 40% and democratise expertise – a beginner operates with an expert's precision.",
        "Connected Worker: The platform directs technicians based on real-time sensor data to where action is needed – productivity increase of 15–30%.",
        "Permanent Audit Readiness: All records are timestamped, immutable (per FDA 21 CFR Part 11) and instantly retrievable. Audit preparation drops by 50%.",
        "Precise Recalls: Digital traceability limits recalls to exactly affected batches – identification in seconds instead of days, massive cost saving.",
      ],
      pl: [
        "Zamykanie luki kompetencyjnej: Cyfrowe instrukcje pracy skracają czas wdrożenia o 40% i demokratyzują wiedzę specjalistyczną – nowicjusz działa z precyzją eksperta.",
        "Połączony pracownik: Platforma kieruje technikami na podstawie danych z czujników w czasie rzeczywistym – wzrost produktywności o 15–30%.",
        "Stała gotowość audytowa: Wszystkie zapisy są oznaczone znacznikiem czasu, niezmienne (zgodnie z FDA 21 CFR Part 11) i natychmiast dostępne. Przygotowanie audytu spada o 50%.",
        "Precyzyjne wycofania: Cyfrowa identyfikowalność ogranicza wycofania do dokładnie dotkniętych partii – identyfikacja w sekundach zamiast w dniach.",
      ],
    },
    table: {
      caption: {
        de: "Indikatoren für die Arbeitseffizienz",
        en: "Workforce Efficiency Indicators",
        pl: "Wskaźniki efektywności pracowników",
      },
      headers: {
        de: ["Wirkungsbereich", "Digitale Maßnahme", "Nutzen"],
        en: ["Area", "Digital Measure", "Benefit"],
        pl: ["Obszar", "Działanie cyfrowe", "Korzyść"],
      },
      rows: {
        de: [
          ["Schulung & Training", "Digitale Arbeitsanweisungen", "40 % Zeitersparnis"],
          ["Montage", "Digitale Arbeitsanweisungen", "20 % Zykluszeit-Reduktion"],
          ["Produktivität", "Digitale Plattformen", "15–30 % Steigerung"],
          ["Indirekte Arbeit", "Fernüberwachung", "Senkung Personalkosten"],
          ["Audit & Compliance", "MES-Dokumentation", "50 % weniger Vorbereitungszeit"],
        ],
        en: [
          ["Training", "Digital Work Instructions", "40% time saving"],
          ["Assembly", "Digital Work Instructions", "20% cycle time reduction"],
          ["Productivity", "Digital Platforms", "15–30% increase"],
          ["Indirect Labour", "Remote Monitoring", "Reduced personnel costs"],
          ["Audit & Compliance", "MES Documentation", "50% less preparation time"],
        ],
        pl: [
          ["Szkolenia", "Cyfrowe instrukcje pracy", "40% oszczędność czasu"],
          ["Montaż", "Cyfrowe instrukcje pracy", "20% redukcja czasu cyklu"],
          ["Wydajność", "Platformy cyfrowe", "15–30% wzrost"],
          ["Praca pośrednia", "Zdalny monitoring", "Obniżenie kosztów personelu"],
          ["Audyt i zgodność", "Dokumentacja MES", "50% mniej czasu przygotowania"],
        ],
      },
    },
  },
];
